import React, { useEffect, useMemo, useState } from 'react';
import Classes from './RecordTable.module.css';
import useScreenWidth from '../../../../../../../Hooks/useScreenWidth';
import SkeletonLoading from '../../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading';
import { Pagination, Stack } from '@mui/material';
import TableRow from './TableRow';
import { useNavigate, useLocation } from 'react-router-dom';
import { AddIcon } from '../../../../../../../utils/Icons';
import { useSelector } from 'react-redux';
import backend from '../../../../../../../api';

const STATUS_DB_TO_UI = {
  completed: "Completed",
  upcoming: "Upcoming",
  cancelled: "Cancelled",
  on_the_way: "On the way",
  delayed: "Delayed",
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function RecordTable() {
  const { pathname } = useLocation();
  const isAppointments = pathname.startsWith('/appointments');

  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;
  const navigateTo = useNavigate();

  const onboarding = useSelector((s) => s.companyInfo?.info);
  const companyId = onboarding?._id;

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [raw, setRaw] = useState([]);
  const [counts, setCounts] = useState({ completed: 0, upcoming: 0, cancelled: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'calendar'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const itemsPerPage = isAppointments ? 8 : 5;

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        const { data } = await backend.get(`/api/bookings/bycompany?companyId=${companyId}`);
        if (!alive) return;
        if (data?.ok) {
          const bookings = data.items || [];
          setRaw(bookings);
          setCounts({
            completed: data.counters?.completed || 0,
            upcoming: data.counters?.upcoming || 0,
            cancelled: data.counters?.cancelled || 0,
          });
          setRows(bookings.map(b => {
            const u = b.userId || {};
            const name = u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';
            const phone = u.phone || '—';
            const addr = [
              b?.location?.address1,
              b?.location?.city,
              b?.location?.region,
              b?.location?.postalCode,
            ].filter(Boolean).join(', ') || '—';
            const dt = b?.appointment?.date ? new Date(b.appointment.date) : null;
            const serviceDate = dt ? dt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

            return {
              _id: b._id,
              serviceDate,
              client: name,
              serviceType: 'Fuel Delivery',
              status: { label: STATUS_DB_TO_UI[b.status] || "Upcoming" },
              address: addr,
              phone,
              dateObj: dt, // for calendar
            };
          }));
        } else {
          setRows([]);
        }
      } catch (e) {
        console.error('fetch bookings error:', e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [companyId, pathname]);

  const totalPages = Math.ceil(rows.length / itemsPerPage);
  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, currentPage, itemsPerPage]);

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, rows.length);
  const totalItems = rows.length;

  function handlePageChange(_e, pageNum) {
    setCurrentPage(pageNum);
  }

  // Calendar logic
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    // Add empty cells for days before the first day
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, events: [] });
    }

    // Add days with events
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const events = rows.filter(row => row.dateObj && 
        row.dateObj.getFullYear() === year && 
        row.dateObj.getMonth() === month && 
        row.dateObj.getDate() === day);
      days.push({ day, events });
    }

    // Add empty cells to fill the last week if needed
    while (days.length % 7 !== 0) {
      days.push({ day: null, events: [] });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const maxVisibleEvents = 3;

  return (
    <div className={Classes.hero}>
      <div className={Classes.table}>
        <div className={Classes.tableWrapper}>
          <div className={Classes.tableBanner}>
            <div className='flexAlign10'>
              {isAppointments && (
                <>
                  <div className={Classes.tableBannerItem}>
                    <h5>Completed</h5>
                    <h4>{counts.completed}</h4>
                  </div>
                  <div className={Classes.tableBannerItem}>
                    <h5>Upcoming</h5>
                    <h4>{counts.upcoming}</h4>
                  </div>
                  <div className={Classes.tableBannerItem}>
                    <h5>Cancelled</h5>
                    <h4>{counts.cancelled}</h4>
                  </div>
                  <div className={Classes.tableBannerItem}>
                    <h5 className={Classes.allText}>All</h5>
                    <h4>{counts.completed + counts.upcoming + counts.cancelled}</h4>
                  </div>
                </>
              )}
              {!isAppointments && (
                <div className={Classes.tableBannerItem}>
                  <h5>Recent Appointments</h5>
                </div>
              )}
              <div className={Classes.viewToggle}>
                <button 
                  className={`${Classes.viewBtn} ${viewMode === 'table' ? Classes.activeView : ''}`}
                  onClick={() => setViewMode('table')}
                >
                  Table
                </button>
                <button 
                  className={`${Classes.viewBtn} ${viewMode === 'calendar' ? Classes.activeView : ''}`}
                  onClick={() => setViewMode('calendar')}
                >
                  Calendar
                </button>
              </div>
            </div>
            <div>
              {isAppointments ? (
                <button className={Classes.addBtn} onClick={() => navigateTo('/add-appointment')}>
                  <AddIcon height='12px' fill='white' />
                  <h5>Add appointment</h5>
                </button>
              ) : (
                <button type="button" className={Classes.viewBtn} onClick={() => navigateTo('/appointments')}>
                  View all {'>'}
                </button>
              )}
            </div>
          </div>

          {viewMode === 'table' ? (
            <>
              <div className={Classes.tableHeader}>
                <div className={`${Classes.headerItem} ${Classes.header1}`}><h5>Service Date</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header2}`}><h5>Client</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header3}`}><h5>Service Type</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header4}`}><h5>Status</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header5}`}><h5>Address</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header6}`}><h5>Phone</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header7}`} />
              </div>

              <div className={Classes.tableBody}>
                {loading ? (
                  <SkeletonLoading length={itemsPerPage} height={isMobile ? '3.5rem' : '2.8rem'} width="100%" br="6px" padding='0' />
                ) : currentItems && currentItems.length > 0 ? (
                  currentItems.map((val, idx) => {
                    const rawIdx = (currentPage - 1) * itemsPerPage + idx;
                    const bookingObj = raw[rawIdx];
                    return (
                      <TableRow
                        key={bookingObj?._id || idx}
                        value={val}
                        index={idx}
                        isLast={idx === currentItems.length - 1}
                        isAppointments={isAppointments}
                        booking={bookingObj}
                        onStatusChanged={(newUiStatus) => {
                          setRows(prev => {
                            const copy = [...prev];
                            const i = (currentPage - 1) * itemsPerPage + idx;
                            if (copy[i]) copy[i] = { ...copy[i], status: { label: newUiStatus } };
                            return copy;
                          });
                        }}
                      />
                    );
                  })
                ) : (
                  <h5 className='placeholderText' style={{ margin: '15px 19px' }} >No appointments yet...</h5>
                )}
              </div>

              {currentItems && currentItems.length > 0 && (
                <div className={Classes.pagFooter}>
                  <h5 style={{ height: '1.2rem' }}>
                    {loading
                      ? <SkeletonLoading height='1.2rem' width='10rem' br='4px' padding='0' />
                      : `Showing ${startItem} to ${endItem} of ${totalItems} results`
                    }
                  </h5>
                  <Stack spacing={2} className={Classes.pagination}>
                    {loading
                      ? <SkeletonLoading height='1.2rem' width='5rem' br='4px' padding='0' />
                      : <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} shape="rounded" />
                    }
                  </Stack>
                </div>
              )}
            </>
          ) : (
            <div className={Classes.calendar}>
              <div className={Classes.calendarHeader}>
                <button className={Classes.calendarNavBtn} onClick={handlePrevMonth}>←</button>
                <h5>{MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</h5>
                <button className={Classes.calendarNavBtn} onClick={handleNextMonth}>→</button>
              </div>
              <div className={Classes.calendarGrid}>
                {DAYS.map((day, idx) => (
                  <div key={idx} className={Classes.calendarDayHeader}>{day}</div>
                ))}
                {calendarDays.map((day, idx) => (
                  <div key={idx} className={`${Classes.calendarDay} ${!day.day ? Classes.emptyDay : ''}`}>
                    {day.day && (
                      <>
                        <span className={Classes.dayNumber}>{day.day}</span>
                        <div className={Classes.eventsContainer}>
                          {day.events.slice(0, maxVisibleEvents).map((event, eventIdx) => (
                            <div
                              key={event._id + eventIdx}
                              className={`${Classes.calendarEvent} ${Classes[event.status.label.toLowerCase().replace(/\s+/g, '_') + 'Event']}`}
                              onClick={() => navigateTo(`/view-appointment/details/${event._id}`)}
                            >
                              <span>{event.client} - {event.serviceType}</span>
                              <span>{event.status.label}</span>
                            </div>
                          ))}
                          {day.events.length > maxVisibleEvents && (
                            <div 
                              className={Classes.seeMore}
                              onClick={() => setSelectedDay(day)}
                            >
                              See {day.events.length - maxVisibleEvents} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedDay && (
        <div className={Classes.calendarPopup}>
          <div className={Classes.popupContent}>
            <h5>Appointments on {selectedDay.day} {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</h5>
            {selectedDay.events.map((event, eventIdx) => (
              <div
                key={event._id + eventIdx}
                className={`${Classes.calendarEvent} ${Classes[event.status.label.toLowerCase().replace(/\s+/g, '_') + 'Event']}`}
                onClick={() => navigateTo(`/view-appointment/details/${event._id}`)}
              >
                <span>{event.client} - {event.serviceType}</span>
                <span>{event.status.label}</span>
              </div>
            ))}
            <button className={Classes.closeBtn} onClick={() => setSelectedDay(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}